import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Download, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  ArrowUpDown, 
  Users,
  Bell,
  Calendar,
  ChevronDown,  
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Calculator

} from 'lucide-react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

import { api } from '../services/api';
import PaymentDetailsModal from './ComptaPaymentDetailsModal';
import AddPaymentModal from './AddPaymentModal';
import SuiviPaiementScolaire from './SuiviPaiementScolaire';
import FamillesNombreuses from './FamillesNombreuses';
import LoadingSpinner from './LoadingSpinner';
import ModalPaiementProrata from './ModalPaiementProrata';
import { useFeatures } from '../contexts/FeaturesContext';
import InfoBulle from './ui/InfoBulle';
import PorteurNotification from './ui/PorteurNotification';
import { useNotification } from '../hooks/useNotification';

// ============================================================================
// LIENS DIRECTS VERS UNE CORRECTION
// ============================================================================
// Le panneau des alertes critiques envoie l'agent sur la fenêtre qui corrige,
// sans le laisser chercher l'élève ou la famille :
//
//   ?tab=paiements&eleve=<id>&ouvrir=reduction            élève hors famille
//   ?tab=paiements&famille=<fid>&eleve=<id>&ouvrir=reduction membre d'une famille
//   ?tab=paiements&eleve=<id>&ouvrir=frais                 frais de début d'année
//   ?tab=paiements&famille=<fid>&ouvrir=modifier           informations de la famille
//
// `tab=paiements` amène GestionComptable sur cet onglet, même s'il en affichait
// un autre (il suit chaque nouvelle navigation) ; le reste n'est lu qu'ici.
// Les paramètres sont lus une fois, puis retirés de l'adresse (remplacement,
// pas de nouvelle entrée d'historique) : un F5 ou un retour ne rouvre rien. Ils
// ne sont retirés qu'une fois la fenêtre ouverte, ou l'échec annoncé : si
// l'écran est démonté entre-temps, le remontage reprend le lien. `tab` reste
// dans l'adresse, comme après tout lien profond.
const PARAMETRES_LIEN_DIRECT = ['eleve', 'famille', 'ouvrir'];

const lireLienDirect = (parametres) => {
  const eleve = parametres?.get('eleve') || null;
  const famille = parametres?.get('famille') || null;
  if (!eleve && !famille) return null;
  const ouvrir = parametres.get('ouvrir') || null;
  return { eleve, famille, ouvrir, signature: `${famille || ''}|${eleve || ''}|${ouvrir || ''}` };
};

// Les frais de début d'année vivent sur l'élève, membre de famille ou non : ils
// s'ouvrent toujours depuis son dossier financier. Le reste d'un lien qui nomme
// une famille se traite dans l'écran des familles nombreuses.
const lienVersFamille = (lien) => Boolean(lien?.famille) && !(lien.ouvrir === 'frais' && lien.eleve);

const LIBELLES_STATUT_ELEVE = {
  radie: 'radié',
  transfere: 'transféré',
  inactif: 'inactif'
};


const GestionPaiementsEleves = () => {
  // « Soldé » atteste qu'un mois a été payé au site d'ou l'eleve vient : le
  // statut n'existe qu'apres un transfert inter-sites. Sans le module, aucun
  // transfert n'est possible, donc aucun eleve ne peut le porter — et l'afficher
  // ne prouverait rien. Il ne faut pas le renommer « Soldé » tout court : un
  // eleve a jour porte deja « Payé », et deux mots pour la meme chose seraient
  // une confusion de plus.
  const { estActive } = useFeatures();
  const interSitesOuvert = estActive('module.intersites');
  // États pour gérer les données et les interactions
  const [activeSection, setActiveSection] = useState('prescolaire');
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([
    { id: 'prescolaire', nom: 'Préscolaire' },
    { id: 'primaire', nom: 'Primaire' },
    { id: 'college', nom: 'Collège' },
    { id: 'lycee', nom: 'Lycée' }
  ]);
  const [activeClass, setActiveClass] = useState(null);
  const [classDetails, setClassDetails] = useState(null);
  const [eleves, setEleves] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingEleves, setIsLoadingEleves] = useState(false);
  // La liste des classes ne change pas d'un mois a l'autre : lue une fois.
  const [classesDeBase, setClassesDeBase] = useState(null);
  // Le mois pour lequel les statistiques en memoire ont ete lues. Tant que le
  // mois affiche n'est pas celui-la, on montre le chargement — jamais le
  // tableau d'un autre mois, meme un instant (le « clignotement »).
  const [statsMoisId, setStatsMoisId] = useState(null);
  // Numero de la derniere demande : une reponse plus ancienne est ignoree.
  const derniereDemandeStats = useRef(0);
  const [anneeActive, setAnneeActive] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);
  const [selectedEleve, setSelectedEleve] = useState(null);
  const [modesPaiement, setModesPaiement] = useState([]);
  const [moisScolaires, setMoisScolaires] = useState([]);
  const [selectedMoisId, setSelectedMoisId] = useState(null);
  const [selectedTrimestre, setSelectedTrimestre] = useState('all');
  const [showSuiviPaiement, setShowSuiviPaiement] = useState(false);
  const [isGestionPaiementsOpen, setIsGestionPaiementsOpen] = useState(true); // Ouvert par défaut
  const [selectedTrimestreForDisplay, setSelectedTrimestreForDisplay] = useState(1); // 🆕 Pour l'affichage dans le header
  const [selectedMoisInTrimestre, setSelectedMoisInTrimestre] = useState(null); // 🆕 Pour filtrer par mois dans le trimestre
  const [showFamillesNombreuses, setShowFamillesNombreuses] = useState(false);
  const [showModalProrata, setShowModalProrata] = useState(false);  
  const [tousLesPaiements, setTousLesPaiements] = useState({});
  const [currentPageEleves, setCurrentPageEleves] = useState(1);
  const ELEVES_PER_PAGE = 20;
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  // Lien direct : la fenêtre de correction à ouvrir dans le dossier financier
  // ('reduction' ou 'frais'), une fois celui-ci affiché.
  const [ouvertureDossier, setOuvertureDossier] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const lienDirect = useMemo(() => lireLienDirect(searchParams), [searchParams]);
  const lienEleveEnCoursRef = useRef(null);
  const { notification, notifier, masquer } = useNotification();



  // Fonctions de navigation par mois
const formatDateForDisplay = (date) => {
  return date.toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric'
  });
};

const goToPreviousMonth = () => {
  const newDate = new Date(selectedDate);
  newDate.setMonth(newDate.getMonth() - 1);
  setSelectedDate(newDate);
};

const goToNextMonth = () => {
  const newDate = new Date(selectedDate);
  newDate.setMonth(newDate.getMonth() + 1);
  setSelectedDate(newDate);
};

const goToCurrentMonth = () => {
  setSelectedDate(new Date());
};

// Fonction pour obtenir le mois_id correspondant au selectedDate
const getMoisIdFromDate = (date) => {
  // Trouver le mois scolaire correspondant à cette date
  const mois = date.getMonth() + 1; // 1-12
  const annee = date.getFullYear();
  
  // Trouver dans moisScolaires le mois correspondant
  const moisScolaire = moisScolaires.find(m => {
    // Logique pour matcher le mois (à adapter selon ta structure)
    // Par exemple si moisScolaires contient {id: 31, nom: 'Octobre', ...}
    return m.nom.toLowerCase() === date.toLocaleDateString('fr-FR', { month: 'long' }).toLowerCase();
  });
  
  return moisScolaire?.id || null;
};

// Le mois affiché appartient-il à l'année scolaire ?
//
// L'année ne suit pas le calendrier : ici elle va d'octobre à juin. En août, le
// mois courant n'existe donc pas comme mois scolaire, `getMoisIdFromDate` rend
// null, et l'écran affichait les classes sans leurs statistiques — un tableau
// entier de zéros, qui se lisait comme « aucun paiement ce mois-ci » au lieu de
// « ce mois n'appartient pas à l'année ».
const moisHorsAnnee = moisScolaires.length > 0 && !getMoisIdFromDate(selectedDate);

const dernierMoisScolaire = moisScolaires.length > 0
  ? [...moisScolaires].sort((a, b) => Number(b.ordre) - Number(a.ordre))[0]
  : null;

// Aller au dernier mois de l'année : la date du 15 tombe dans sa fenêtre quel
// que soit le mois, ce qui évite de reconstruire une date depuis les bornes.
const allerAuDernierMois = () => {
  if (!dernierMoisScolaire?.date_debut) return;
  const debut = new Date(dernierMoisScolaire.date_debut);
  setSelectedDate(new Date(debut.getFullYear(), debut.getMonth(), 15));
};




  // Effet pour charger l'année scolaire active
  useEffect(() => {
    const fetchAnneeActive = async () => {
      try {
        const response = await api.anneesScolaires.getActive();
        if (response.success && response.data) {
          setAnneeActive(response.data);
        } else {
          console.error("Aucune année scolaire active trouvée");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération de l'année scolaire active:", error);
      }
    };

    fetchAnneeActive();
  }, []);


 

  // 1) Les classes, une seule fois pour l'année.
  //
  // Elles etaient relues a chaque changement de mois, en plus des statistiques :
  // deux appels la ou un suffit, et une attente plus longue a chaque clic.
useEffect(() => {
  if (!anneeActive) return;

  const fetchClasses = async () => {
    try {
      const allClasses = await api.classes.getAll();
      const classesWithSection = allClasses.map(classe => {
        let section = 'primaire';
        switch(classe.section_id) {
          case 1: section = 'prescolaire'; break;
          case 2: section = 'primaire'; break;
          case 3: section = 'college'; break;
          case 4: section = 'lycee'; break;
        }
        return { ...classe, section };
      });
      setClassesDeBase(classesWithSection);
    } catch (error) {
      console.error("❌ Erreur chargement classes:", error);
      setClassesDeBase([]);
    }
  };

  fetchClasses();
}, [anneeActive]);

  // 2) Les statistiques du mois affiché, à chaque changement de mois.
useEffect(() => {
  if (!anneeActive || moisScolaires.length === 0 || classesDeBase === null) return;

  const moisId = getMoisIdFromDate(selectedDate);

  // Hors de l'année scolaire : pas de statistiques, le bandeau le dit.
  if (!moisId) {
    setClasses(classesDeBase);
    setStatsMoisId(null);
    setIsLoading(false);
    return;
  }

  const demande = ++derniereDemandeStats.current;

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const response = await api.finance.getStatsPaiementsGlobales(anneeActive.id, moisId);

      // Un clic plus récent a déjà lancé une autre demande : celle-ci n'a plus cours.
      if (demande !== derniereDemandeStats.current) return;

      if (response.success) {
        const classesAvecStats = classesDeBase.map(classe => {
          const stats = response.data.find(s => s.classe_id === classe.id);

          if (stats) {
            const tauxRecouvrement = stats.effectif > 0
              ? Math.round((stats.payes / stats.effectif) * 100)
              : 0;

            return {
              ...classe,
              effectif: stats.effectif,
              payes: stats.payes,
              payesProrata: stats.payes_prorata || 0,
              soldes: stats.soldes || 0,
              partiels: stats.partiels,
              nonPayes: stats.non_payes,
              nonScolarises: stats.non_scolarises,
              tauxRecouvrement
            };
          }

          return {
            ...classe,
            effectif: 0,
            payes: 0,
            payesProrata: 0,
            soldes: 0,
            partiels: 0,
            nonPayes: 0,
            nonScolarises: 0,
            tauxRecouvrement: 0
          };
        });

        setClasses(classesAvecStats);
      } else {
        setClasses(classesDeBase);
      }
      setStatsMoisId(moisId);
    } catch (error) {
      if (demande !== derniereDemandeStats.current) return;
      console.error("❌ Erreur chargement statistiques:", error);
      setClasses(classesDeBase);
      setStatsMoisId(moisId);
    } finally {
      if (demande === derniereDemandeStats.current) setIsLoading(false);
    }
  };

  fetchStats();
}, [anneeActive, moisScolaires.length, selectedDate, classesDeBase]);



  // Effet pour charger les modes de paiement
  useEffect(() => {
    const fetchModesPaiement = async () => {
      try {
        const response = await api.finance.getModesPaiement();
        if (response.success && response.data) {
          setModesPaiement(response.data);
        } else {
          console.error("Erreur lors de la récupération des modes de paiement");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des modes de paiement:", error);
      }
    };

    fetchModesPaiement();
  }, []);



//CHARGER LES MOIS SCOLAIRES
  useEffect(() => {
  const fetchMoisScolaires = async () => {
    if (!anneeActive) return;
    
    try {
      const response = await api.moisScolaires.getAll();
      if (response.success && response.data) {
        setMoisScolaires(response.data);
        
        // Sélectionner le premier mois par défaut
        if (response.data.length > 0) {
          setSelectedMoisId(response.data[0].id);
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des mois scolaires:", error);
    }
  };
  
  fetchMoisScolaires();
}, [anneeActive]);




// Réinitialiser la pagination quand on change de classe
useEffect(() => {
  setCurrentPageEleves(1);
}, [activeClass]);

// Réinitialiser la pagination quand on change les filtres
useEffect(() => {
  setCurrentPageEleves(1);
}, [searchTerm, filterStatus, selectedMoisInTrimestre]);



  // 🆕 Initialiser le mois sélectionné avec le premier mois du trimestre actuel
// 🆕 Toujours sélectionner le premier mois du trimestre quand on change de trimestre
useEffect(() => {
  if (moisScolaires.length > 0) {
    const premierMoisDuTrimestre = moisScolaires.find(
      m => m.trimestre === selectedTrimestreForDisplay
    );
    if (premierMoisDuTrimestre) {
      setSelectedMoisInTrimestre(premierMoisDuTrimestre.id);
    }
  }
}, [moisScolaires, selectedTrimestreForDisplay]); 



 
// Fonction pour sélectionner une classe
const handleClassSelect = async (classe) => {
  // Si on clique sur la même classe, on la ferme
  if (activeClass?.id === classe.id) {
    setActiveClass(null);
    setEleves([]); // ✅ Vider les élèves
    setTousLesPaiements({}); // ✅ Vider les paiements
    return;
  }
  
  // Fermer le suivi paiement si ouvert
  setShowSuiviPaiement(false);
  
  // ✅ Définir la classe active AVANT de charger
  setActiveClass(classe);
  setSearchTerm('');
  setFilterStatus('all');
  
  try {
    setIsLoadingEleves(true);
    
    console.log(`🚀 Chargement élèves classe ${classe.id}`);
    
    // 1️⃣ Charger les élèves avec leurs paiements
    const response = await api.finance.getElevesClasseAvecPaiements(
      classe.id,
      anneeActive.id
    );
    
    if (response.success) {
      // Préparer les paiements par élève
      const paiementsParEleve = {};
      response.data.forEach(eleve => {
        paiementsParEleve[eleve.id] = eleve.paiements;
      });
      
      setTousLesPaiements(paiementsParEleve);
      
      // Préparer les élèves
      const elevesAvecInfos = response.data.map(eleve => ({
        ...eleve,
        classe_nom: classe.nom,
        parent_nom: eleve.parent_nom || "Non renseigné",
        parent_telephone: eleve.parent_telephone || "Non renseigné"
      }));
      
      setEleves(elevesAvecInfos);
      console.log(`✅ ${elevesAvecInfos.length} élèves chargés`);
    }
    
    // 2️⃣ 🆕 RECHARGER AUSSI LES STATS GLOBALES DE CETTE CLASSE
    const moisId = getMoisIdFromDate(selectedDate);
    if (moisId) {
      console.log("🔄 Rechargement des stats globales de la classe...");
      
      const statsResponse = await api.finance.getStatsPaiementsGlobales(
        anneeActive.id,
        moisId
      );
      
      if (statsResponse.success) {
        // Mettre à jour uniquement cette classe dans le tableau
        setClasses(prevClasses => 
          prevClasses.map(c => {
            if (c.id === classe.id) {
              const stats = statsResponse.data.find(s => s.classe_id === c.id);
              
              if (stats) {
                const tauxRecouvrement = stats.effectif > 0 
                  ? Math.round((stats.payes / stats.effectif) * 100) 
                  : 0;
                
               return {
                  ...c,
                  effectif: stats.effectif,
                  payes: stats.payes,
                  payesProrata: stats.payes_prorata || 0,
                  soldes: stats.soldes || 0,
                  partiels: stats.partiels,
                  nonPayes: stats.non_payes,
                  nonScolarises: stats.non_scolarises,
                  tauxRecouvrement
                };
              }
            }
            return c;
          })
        );
        
        console.log("✅ Stats globales de la classe mises à jour");
      }
    }
    
    setIsLoadingEleves(false);
  } catch (error) {
    console.error('❌ Erreur chargement élèves:', error);
    setIsLoadingEleves(false);
  }
};



const getElevesAvecCalculs = () => {
  console.log("\n=== 🔍 DEBUG getElevesAvecCalculs ===");
  console.log("1️⃣ activeClass:", activeClass);
  console.log("2️⃣ Nombre d'élèves:", eleves.length);
  console.log("3️⃣ selectedMoisInTrimestre:", selectedMoisInTrimestre);
  
  if (!activeClass || !eleves.length) {
    console.log("❌ ARRÊT : Pas de classe active ou pas d'élèves");
    return [];
  }
  
  const resultat = eleves.map((eleve, index) => {
    console.log(`\n👤 === ÉLÈVE ${index + 1}/${eleves.length} : ${eleve.prenom} ${eleve.nom} (ID: ${eleve.id}) ===`);
    
    const paiements = tousLesPaiements[eleve.id] || [];
    console.log(`   📦 Paiements trouvés:`, paiements.length, "paiements");
    
    let montantDu = 0;
    let montantPaye = 0;
    let statut = 'Non payé';
    
    // ✅ UNIQUEMENT : Un seul mois spécifique
    if (selectedMoisInTrimestre) {
      const paiementMois = paiements.find(p => p.mois_id === parseInt(selectedMoisInTrimestre));
      console.log(`   🔍 Paiement trouvé pour mois ${selectedMoisInTrimestre}:`, paiementMois ? "OUI" : "NON");
      
      if (paiementMois) {
      console.log(`   📦 Détail du paiement:`, paiementMois);
      console.log(`   💰 Montant attendu: ${paiementMois.montant_attendu}`);
      console.log(`   💵 Montant payé: ${paiementMois.montant_paye}`);
      console.log(`   📊 Statut BDD: "${paiementMois.statut}"`);
      console.log(`   🏫 Statut fréquentation: "${paiementMois.statut_frequentation}"`);
      
      montantDu = paiementMois.montant_attendu || 0;
      montantPaye = paiementMois.montant_paye || 0;
      
      // 🆕 CORRECTION : Si "non scolarisé", afficher "Non scolarisé" au lieu de "Non payé"
      if (paiementMois.statut_frequentation === 'non_scolarise') {
        statut = 'Non scolarisé';
        console.log(`   ✅ STATUT FINAL: "Non scolarisé" (détecté via statut_frequentation)`);
      } else {
        statut = paiementMois.statut || 'Non payé';
        console.log(`   ✅ STATUT FINAL: "${statut}" (lu depuis BDD)`);
      }
    }


    }

    
    
    const montantRestant = montantDu - montantPaye;
    
    console.log(`\n   📊 RÉSULTAT: statut="${statut}", montantDu=${montantDu}, montantPaye=${montantPaye}`);
    
    return {
      ...eleve,
      statut_paiement: statut,
      montant_du: montantDu,
      montant_paye: montantPaye,
      montant_restant: montantRestant
    };
  });
  
  console.log("\n✅ FIN getElevesAvecCalculs - Élèves retournés:", resultat.length);
  return resultat;
};


// 🆕 Obtenir les élèves avec calculs selon les filtres
const elevesAvecCalculs = getElevesAvecCalculs();

// Fonction pour filtrer les élèves
const filteredEleves = elevesAvecCalculs.filter(eleve => {
  const matchesSearch = searchTerm === '' || 
    eleve.nom.toLowerCase().includes(searchTerm.toLowerCase()) || 
    eleve.prenom.toLowerCase().includes(searchTerm.toLowerCase()) || 
    eleve.matricule.toLowerCase().includes(searchTerm.toLowerCase());
  
  const matchesFilter = filterStatus === 'all' || 
   (filterStatus === 'paye' && (eleve.statut_paiement === 'Payé' || eleve.statut_paiement === 'Soldé')) ||
    (filterStatus === 'paye_prorata' && eleve.statut_paiement === 'Payé au prorata') ||  // 🆕 AJOUTÉ
    (filterStatus === 'partiel' && eleve.statut_paiement === 'Partiel') ||
    (filterStatus === 'non_paye' && eleve.statut_paiement === 'Non payé') ||
    (filterStatus === 'Non scolarisé' && eleve.statut_paiement === 'Non scolarisé');
  
  return matchesSearch && matchesFilter;
});

  // Fonction pour voir les détails d'un élève
  const handleViewDetails = (eleve) => {
    setSelectedEleve(eleve);
    setIsDetailsModalOpen(true);
  };

  // Fonction pour ajouter un paiement
  const handleAddPayment = (eleve = null) => {
    setSelectedEleve(eleve);
    setIsAddPaymentModalOpen(true);
  };


  // ========== LIENS DIRECTS (panneau des alertes critiques) ==========
  // Retire le lien de l'adresse, sans entrée d'historique. On relit l'adresse
  // du moment plutôt que celle du dernier rendu : si un autre lien est arrivé
  // entre-temps, c'est lui qui reste à traiter, on n'y touche pas.
  const retirerLienDirect = useCallback((signature) => {
    const actuels = new URLSearchParams(window.location.search);
    if (signature && lireLienDirect(actuels)?.signature !== signature) return;
    PARAMETRES_LIEN_DIRECT.forEach((cle) => actuels.delete(cle));
    setSearchParams(actuels, { replace: true });
  }, [setSearchParams]);

  const fermerDossierFinancier = () => {
    setIsDetailsModalOpen(false);
    setOuvertureDossier(null);
  };

  // Ouvre le dossier financier de l'élève désigné par le lien, puis la fenêtre
  // de correction demandée. Les données de l'élève viennent de sa fiche : la
  // classe, le parent et la famille dont le dossier a besoin y sont tous.
  const ouvrirDossierParLien = async ({ eleve: eleveParam, ouvrir, signature }) => {
    const echouer = (message) => {
      notifier(message, 'error', { long: true, title: 'Correction impossible' });
      retirerLienDirect(signature);
    };

    const eleveId = Number.parseInt(eleveParam, 10);
    if (!Number.isInteger(eleveId) || eleveId <= 0) {
      echouer("Le lien de correction ne désigne aucun élève valide. Rien n'a été ouvert.");
      return;
    }

    notifier('Ouverture du dossier financier…', 'loading');
    let fiche = null;
    let motifEchec = '';
    try {
      fiche = await api.eleves.getById(eleveId);
      if (fiche?.success === false) {
        // Refus métier (licence fermée, droit manquant) : le serveur dit
        // pourquoi, on y ajoute la conclusion commune aux échecs du lien.
        const texte = String(fiche.message || fiche.error || '').trim();
        if (texte) motifEchec = `${texte}${/[.!?]$/.test(texte) ? '' : '.'} Rien n'a été ouvert.`;
        fiche = null;
      }
    } catch (error) {
      fiche = null;
      // Seul un 404 dit que l'élève n'existe plus ; une erreur du serveur, une
      // coupure réseau ou une session expirée n'en disent rien.
      if (error?.status === 404) {
        motifEchec = "Cet élève est introuvable : il a peut-être été supprimé. Rien n'a été ouvert.";
      } else if (error?.status === 401) {
        motifEchec = "Votre session a expiré : reconnectez-vous, puis rouvrez le lien. Rien n'a été ouvert.";
      } else {
        motifEchec = "La fiche de cet élève n'a pas pu être lue (erreur du serveur ou du réseau). "
          + "Réessayez dans un instant. Rien n'a été ouvert.";
      }
    }

    if (!fiche?.id) {
      echouer(motifEchec
        || "La fiche de cet élève n'a pas pu être lue. Réessayez dans un instant. Rien n'a été ouvert.");
      return;
    }

    const nomEleve = `${fiche.prenom || ''} ${fiche.nom || ''}`.trim() || 'Cet élève';

    if (Number(fiche.annee_scolaire_id) !== Number(anneeActive.id)) {
      echouer(`${nomEleve} est inscrit sur une autre année scolaire que celle affichée`
        + `${anneeActive.nom ? ` (${anneeActive.nom})` : ''}. `
        + "Reconnectez-vous sur son année pour le corriger. Rien n'a été ouvert.");
      return;
    }

    const statut = String(fiche.statut || '').toLowerCase();
    if (statut !== 'actif') {
      echouer(`${nomEleve} n'est plus actif (${LIBELLES_STATUT_ELEVE[statut] || statut || 'statut inconnu'}) : `
        + "son dossier ne se corrige plus d'ici. Rien n'a été ouvert.");
      return;
    }

    // La réduction mensuelle d'un membre de famille se modifie depuis sa
    // famille : le dossier financier n'en propose pas le bouton. Le lien est
    // réécrit vers la famille, qui fait le reste.
    if (ouvrir === 'reduction' && fiche.famille_id) {
      masquer();
      const actuels = new URLSearchParams(window.location.search);
      if (lireLienDirect(actuels)?.signature !== signature) return;
      actuels.set('famille', String(fiche.famille_id));
      setSearchParams(actuels, { replace: true });
      return;
    }

    masquer();
    setIsAddPaymentModalOpen(false);
    setSelectedEleve({
      ...fiche,
      parent_nom: fiche.parent_nom || 'Non renseigné',
      parent_telephone: fiche.parent_telephone || 'Non renseigné'
    });
    setOuvertureDossier(['reduction', 'frais'].includes(ouvrir) ? ouvrir : null);
    setIsDetailsModalOpen(true);
    retirerLienDirect(signature);
  };

  // Un lien qui nomme une famille ouvre l'écran des familles nombreuses ; c'est
  // lui qui ouvre la famille et la fenêtre demandée, puis consomme le lien.
  useEffect(() => {
    if (!lienVersFamille(lienDirect)) return;
    setShowSuiviPaiement(false);
    setShowModalProrata(false);
    setActiveClass(null);
    setIsAddPaymentModalOpen(false);
    fermerDossierFinancier();
    setShowFamillesNombreuses(true);
  }, [lienDirect?.signature]);

  // Un lien qui nomme un élève seul ouvre son dossier financier, une fois
  // l'année connue. Le repère évite de traiter deux fois le même lien (double
  // rendu, mode strict) ; il retombe quand le lien a quitté l'adresse, pour
  // qu'un second clic sur le même lien soit suivi.
  useEffect(() => {
    if (!lienDirect) {
      lienEleveEnCoursRef.current = null;
      return;
    }
    if (lienVersFamille(lienDirect) || !anneeActive?.id) return;
    if (lienEleveEnCoursRef.current === lienDirect.signature) return;
    lienEleveEnCoursRef.current = lienDirect.signature;
    ouvrirDossierParLien(lienDirect);
  }, [lienDirect?.signature, anneeActive?.id]);


  // Fonction pour formater les montants
  // Remplacez votre fonction formatMontant actuelle par celle-ci
const formatMontant = (montant) => {
    return `${Math.round(montant).toLocaleString('fr-FR').replace(/,/g, ' ')} FCFA`;
  };


  // Fonction pour obtenir les mois du trimestre sélectionné
const isEleveInFamilleNombreuse = (eleve) => {
  return Boolean(
    eleve?.est_en_famille_nombreuse ||
    (
      eleve?.famille_nombreuse_id &&
      ['active', 'a_regulariser'].includes(String(eleve?.famille_statut || '').toLowerCase())
    )
  );
};

const getFamilleEleveNom = (eleve) => {
  return eleve?.famille_nom || 'Famille nombreuse';
};

const getFamillePaymentMessage = (eleve) => {
  return `Cet eleve appartient a ${getFamilleEleveNom(eleve)}. Les paiements doivent etre geres depuis le dossier de la famille.`;
};

const getMoisDuTrimestre = () => {
  return moisScolaires.filter(mois => mois.trimestre === selectedTrimestreForDisplay);
};




// 🆕 NOUVELLE FONCTION : Calculer les stats selon les filtres actuels
const calculerStatsAvecFiltres = () => {
  if (!activeClass || !eleves.length) return null;
  
 const stats = {
    effectif: eleves.length,
    payes: 0,
    payesProrata: 0,
    soldes: 0,
    partiels: 0,
    nonPayes: 0,
    nonScolarises: 0
  };
  
  const elevesAvecCalculs = getElevesAvecCalculs();
  elevesAvecCalculs.forEach(eleve => {
    if (eleve.statut_paiement === 'Payé') {
      stats.payes++;
    } else if (eleve.statut_paiement === 'Soldé') {
      stats.soldes++;

    } else if (eleve.statut_paiement === 'Payé au prorata') {  // 🆕 Nouveau cas
      stats.payesProrata++;
    } else if (eleve.statut_paiement === 'Partiel') {
      stats.partiels++;
    } else if (eleve.statut_paiement === 'Non scolarisé') {
      stats.nonScolarises++;
    } else {
      stats.nonPayes++;
    }
  });
  
  const tauxRecouvrement = stats.effectif > 0 
    ? Math.round((stats.payes / stats.effectif) * 100) 
    : 0;
  
  return {
    ...stats,
    tauxRecouvrement
  };
};




  // Rendu du tableau des classes pour la section active
const renderClassesTable = () => {
    const classesInSection = classes.filter(classe => classe.section === activeSection);
    
    // Le mois affiché n'est pas encore celui des statistiques en mémoire : on
    // attend, plutôt que de montrer un instant les chiffres d'un autre mois.
    const moisIdAffiche = getMoisIdFromDate(selectedDate);
    const statsEnRetard = Boolean(moisIdAffiche) && moisIdAffiche !== statsMoisId;
    if (isLoading || statsEnRetard) {
      return <LoadingSpinner message="Chargement des classes..." />;
    }

if (classesInSection.length === 0) {
  return (
    <div className="text-center py-8 text-gray-500">
      Aucune classe trouvée dans cette section.
    </div>
  );
}
    
    // Hors de l'année scolaire, il n'y a rien à compter : on le dit, plutôt que
    // d'afficher un tableau de zéros qui ressemble à des statistiques.
    if (moisHorsAnnee) {
      const nomMois = selectedDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      const premier = moisScolaires.length > 0
        ? [...moisScolaires].sort((a, b) => Number(a.ordre) - Number(b.ordre))[0]
        : null;

      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                {nomMois.charAt(0).toUpperCase() + nomMois.slice(1)} ne fait pas partie de l'année scolaire
                {anneeActive?.nom ? ` ${anneeActive.nom}` : ''}.
              </p>
              <p className="mt-1 text-sm text-amber-800">
                {premier && dernierMoisScolaire
                  ? `Elle va de ${premier.nom} à ${dernierMoisScolaire.nom}. Aucun paiement n'est enregistré en dehors de cette période.`
                  : "Aucun paiement n'est enregistré en dehors de la période scolaire."}
              </p>
              {dernierMoisScolaire && (
                <Button
                  type="button"
                  onClick={allerAuDernierMois}
                  className="mt-3 h-9 min-w-[168px] justify-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a]"
                >
                  <Calendar className="h-4 w-4" />
                  Voir {String(dernierMoisScolaire.nom || '').toLowerCase()}
                  {dernierMoisScolaire.date_debut ? ` ${new Date(dernierMoisScolaire.date_debut).getFullYear()}` : ''}
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">

          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2 text-left">Classe</th>
              <th className="px-4 py-2 text-center">Effectif</th>
             <th className="px-4 py-2 text-center">Payé</th>
              <th className="px-4 py-2 text-center">Prorata</th>
              <th className="px-4 py-2 text-center">Soldé</th>
              <th className="px-4 py-2 text-center">Partiel</th>
              <th className="px-4 py-2 text-center">Non payé</th>
              <th className="px-4 py-2 text-center">Non scolarisé</th>  {/* ✅ AVANT */}
              <th className="px-4 py-2 text-center">Taux recouvrement</th>  {/* ✅ APRÈS */}
              <th className="px-4 py-2 text-center w-[150px]">Actions</th>
            </tr>
          </thead>


          <tbody>
            {classesInSection.map((classe) => (


             <tr 
                  key={classe.id} 
                  className={`border-b hover:bg-gray-50 transition-all duration-300 ease-in-out ${
                    activeClass?.id === classe.id 
                      ? 'bg-blue-50 border-l-4 border-l-[#246990] shadow-md' // 🆕 RETIRER scale-[1.01]
                      : ''
                  }`}
                >
                  <td className="px-4 py-3">{classe.nom}</td>
                  <td className="px-4 py-3 text-center">
                    {classe.effectif || 0}
                  </td>

                  <td className="px-4 py-3 text-center">
                    <span className="bg-green-100 text-green-800 py-1 px-3 rounded-full text-xs font-medium">
                      {classe.payes || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-blue-100 text-blue-800 py-1 px-3 rounded-full text-xs font-medium">
                      {classe.payesProrata || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-teal-100 text-teal-800 py-1 px-3 rounded-full text-xs font-medium">
                      {classe.soldes || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-yellow-100 text-yellow-800 py-1 px-3 rounded-full text-xs font-medium">
                      {classe.partiels || 0}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-center">
                    <span className="bg-red-100 text-red-800 py-1 px-3 rounded-full text-xs font-medium">
                      {classe.nonPayes || 0}
                    </span>
                  </td>

                  {/* 🆕 Nouvelle cellule "Non scolarisé" */}
                  <td className="px-4 py-3 text-center">
                    <span className="bg-gray-200 text-gray-700 py-1 px-3 rounded-full text-xs font-medium">
                      {classe.nonScolarises || 0}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-center">
                    <span className="bg-blue-100 text-blue-800 py-1 px-2 rounded-md text-xs font-medium">
                      {classe.tauxRecouvrement || 0}%
                    </span>
                  </td>


                  <td className="px-4 py-3"> {/* 🆕 RETIRER text-center et w-[150px] */}
                    <div className="flex justify-center items-center min-w-[130px]"> {/* 🆕 Ajouter min-w */}
                      <Button 
                        variant="primary" 
                        size="sm"
                        className={`flex items-center justify-center gap-2 text-white min-w-[110px] ${
                          activeClass?.id === classe.id 
                            ? 'bg-[#c66d59] hover:bg-[#ae7467]/90' 
                            : 'bg-[#246990] hover:bg-[#1a4d6e]'
                        }`}
                        onClick={() => handleClassSelect(classe)}
                      >
                        {activeClass?.id === classe.id ? (
                          <>
                            <ChevronUp size={16} />
                            Fermer
                          </>
                        ) : (
                          <>
                            <ChevronDown size={16} />
                            Gérer
                          </>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>

            ))}
          </tbody>
        </table>
      </div>
    );
  };


  // Rendu du tableau des élèves pour la classe active
 const renderElevesTable = () => {
  if (!activeClass) return null;
  
  if (isLoadingEleves) {
  return <LoadingSpinner message="Chargement des élèves..." />;
}
  
  if (filteredEleves.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Aucun élève trouvé avec les critères actuels.
      </div>
    );
  }
  
  // 🆕 Calcul de la pagination
  const totalPages = Math.ceil(filteredEleves.length / ELEVES_PER_PAGE);
  const startIndex = (currentPageEleves - 1) * ELEVES_PER_PAGE;
  const endIndex = startIndex + ELEVES_PER_PAGE;
  const elevesPage = filteredEleves.slice(startIndex, endIndex);
  
  return (
    <div className="mt-4">
      {/* Tableau des élèves */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2 text-left">Élève</th>
              <th className="px-4 py-2 text-left">Matricule</th>
              <th className="px-4 py-2 text-left">Famille</th>
              <th className="px-4 py-2 text-center">Statut</th>
              <th className="px-4 py-2 text-right">Montant dû</th>
              <th className="px-4 py-2 text-right">Payé</th>
              <th className="px-4 py-2 text-right">Restant</th>
              <th className="px-4 py-2 text-center w-[180px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {elevesPage.map((eleve) => ( // 🆕 Utiliser elevesPage au lieu de filteredEleves
              <tr 
                key={eleve.id} 
                className={`border-b hover:bg-gray-50 transition-colors ${eleve.id === selectedEleve?.id ? 'bg-blue-50' : ''}`}
              >
                <td className="px-4 py-3">
                  <span>{eleve.prenom} {eleve.nom}</span>
                </td>
                <td className="px-4 py-3">{eleve.matricule}</td>
                <td className="px-4 py-3">
                  {isEleveInFamilleNombreuse(eleve) ? (
                    <div className="flex flex-col gap-1">
                      <span className="inline-flex w-fit items-center gap-1 rounded-md border border-[#246990]/20 bg-[#246990]/10 px-2 py-1 text-xs font-semibold text-[#246990]">
                        <Users size={12} />
                        {getFamilleEleveNom(eleve)}
                      </span>
                      <span className="text-[11px] leading-tight text-gray-500">
                        Paiement via famille nombreuse
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">Individuel</span>
                  )}
                </td>



                <td className="px-4 py-3 text-center">
                {eleve.statut_paiement === 'Payé' && (
                  <span className="mx-auto inline-flex h-6 w-[150px] items-center justify-center gap-1 rounded-full text-xs font-medium whitespace-nowrap bg-green-100 text-green-800">
                    <CheckCircle size={12} />
                    Payé
                  </span>
                )}
                {eleve.statut_paiement === 'Payé au prorata' && (
                  <span className="mx-auto inline-flex h-6 w-[150px] items-center justify-center gap-1 rounded-full text-xs font-medium whitespace-nowrap bg-blue-100 text-blue-800">
                    <Calculator size={12} />
                    Payé au prorata
                  </span>
                )}
                {eleve.statut_paiement === 'Partiel' && (
                  <span className="mx-auto inline-flex h-6 w-[150px] items-center justify-center gap-1 rounded-full text-xs font-medium whitespace-nowrap bg-yellow-100 text-yellow-800">
                    <AlertCircle size={12} />
                    Partiel
                  </span>
                )}
                {eleve.statut_paiement === 'Non payé' && (
                  <span className="mx-auto inline-flex h-6 w-[150px] items-center justify-center gap-1 rounded-full text-xs font-medium whitespace-nowrap bg-red-100 text-red-800">
                    Non payé
                  </span>
                )}

                {interSitesOuvert && eleve.statut_paiement === 'Soldé' && (
                  <span className="mx-auto inline-flex h-6 w-[150px] items-center justify-center gap-1 rounded-full text-xs font-medium whitespace-nowrap cursor-help bg-teal-100 text-teal-800"
                    title="Ce mois a été réglé sur l'ancien établissement de l'élève avant son transfert inter-sites"
                  >
                    <CheckCircle size={12} />
                    Soldé (site source)
                  </span>
                )}

                {eleve.statut_paiement === 'Non scolarisé' && (
                  <span className="mx-auto inline-flex h-6 w-[150px] items-center justify-center gap-1 rounded-full text-xs font-medium whitespace-nowrap bg-gray-200 text-gray-700">
                    Non scolarisé
                  </span>
                )}
              </td>



                <td className="px-4 py-3 text-right">{formatMontant(eleve.montant_du || 0)}</td>
                <td className="px-4 py-3 text-right">{formatMontant(eleve.montant_paye || 0)}</td>
                <td className="px-4 py-3 text-right">{formatMontant(eleve.montant_restant || 0)}</td>

                <td className="px-4 py-3 text-center align-top">
                <div className="mx-auto flex w-[165px] flex-col items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-[150px] justify-center gap-2 border-[#246990]/30 text-[#246990] hover:bg-[#eef7fb] hover:text-[#246990]"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(eleve);
                    }}
                  >
                    <Eye size={16} />
                    Détails
                  </Button>

                  {isEleveInFamilleNombreuse(eleve) ? (
                    <span className="block" title={getFamillePaymentMessage(eleve)}>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="h-8 w-[150px] justify-center gap-2 border-[#246990]/20 text-[#246990]/60"
                      >
                        <Users size={16} />
                        Paiement famille
                      </Button>
                    </span>
                  ) : (
                  <Button
                    size="sm"
                    className="h-8 w-[150px] justify-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a]"
                    onClick={() => handleAddPayment(eleve)}
                  >
                    <Plus size={16} />
                    Payer
                  </Button>
                  )}
                </div>
              </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 🆕 Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between border-t pt-4">
          <div className="text-sm text-gray-600">
            Affichage de {startIndex + 1} à {Math.min(endIndex, filteredEleves.length)} sur {filteredEleves.length} élèves
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPageEleves(prev => Math.max(1, prev - 1))}
              disabled={currentPageEleves === 1}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Précédent
            </Button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                // Afficher uniquement les pages proches de la page actuelle
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPageEleves - 1 && page <= currentPageEleves + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPageEleves(page)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        currentPageEleves === page
                          ? 'bg-[#246990] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (
                  page === currentPageEleves - 2 ||
                  page === currentPageEleves + 2
                ) {
                  return <span key={page} className="px-2 text-gray-400">...</span>;
                }
                return null;
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPageEleves(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPageEleves === totalPages}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};





 return (
  <div className="w-full">
    {/* SI on affiche le Suivi Paiement */}
    {showSuiviPaiement && (
      <SuiviPaiementScolaire 
        onClose={() => setShowSuiviPaiement(false)} 
      />
    )}

    {/* SI on affiche Familles Nombreuses */}
    {showFamillesNombreuses && (
      <FamillesNombreuses
        onClose={() => setShowFamillesNombreuses(false)}
        lienDirect={lienVersFamille(lienDirect) ? lienDirect : null}
        onLienDirectTraite={retirerLienDirect}
      />
    )}

    {/* SI on n'affiche ni Suivi ni Familles, afficher les Tabs */}
    {!showSuiviPaiement && !showFamillesNombreuses && (
      <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full">



        {/* Section de navigation avec onglets à gauche et boutons à droite */}
        <div className="flex justify-between items-center mb-4">
          {/* Onglets à gauche */}
          <TabsList className="bg-[#c66d59]/10 p-1 rounded-lg">
            {sections.map(section => (
              <TabsTrigger 
                key={section.id} 
                value={section.id}
                className="data-[state=active]:bg-[#c66d59] data-[state=active]:text-white"
              >
                {section.nom}
              </TabsTrigger>
            ))}
          </TabsList>
          
          {/* Boutons à droite */}
          {/* Boutons à droite */}
          <div className="flex items-center gap-2">
            {/* Bouton Suivi paiement scolaire */}
            <Button
              size="sm"
              onClick={() => {
                setShowSuiviPaiement(!showSuiviPaiement);
                setShowFamillesNombreuses(false);
                setShowModalProrata(false);  // 🆕 AJOUTER
                setActiveClass(null);
              }}
              className="bg-[#246990] hover:bg-[#246990]/90 text-white font-semibold flex items-center gap-2"
            >
              {showSuiviPaiement ? (
                <>
                  <ChevronUp size={16} />
                  Fermer suivi
                </>
              ) : (
                <>
                  <ChevronDown size={16} />
                  Suivi paiement scolaire
                </>
              )}
            </Button>

            {/* Bouton Familles nombreuses */}
            <Button
              size="sm"
              onClick={() => {
                setShowFamillesNombreuses(!showFamillesNombreuses);
                setShowSuiviPaiement(false);
                setShowModalProrata(false);  // 🆕 AJOUTER
                setActiveClass(null);
              }}
              className="bg-[#246990] hover:bg-[#246990]/90 text-white font-semibold flex items-center gap-2"
            >
              {showFamillesNombreuses ? (
                <>
                  <ChevronUp size={16} />
                  Fermer familles
                </>
              ) : (
                <>
                  <ChevronDown size={16} />
                  Familles nombreuses
                </>
              )}
            </Button>

            {/* 🆕 NOUVEAU BOUTON AU PRORATA */}
            <Button
              size="sm"
              onClick={() => setShowModalProrata(true)}
              className="bg-[#246990] hover:bg-[#246990] text-white font-semibold flex items-center gap-2"
            >
              <Calculator size={16} />
              Au Prorata
            </Button>
          </div>
        </div>



        
        {/* Contenu pour chaque section */}
        
        {sections.map(section => (

          <TabsContent key={section.id} value={section.id} className="mt-0">
          <Card className="border-0 shadow-sm">
            <CardHeader 
              className="bg-[#246990] text-white rounded-t-lg cursor-pointer hover:bg-[#1a4d6e] transition-colors"
              onClick={() => setIsGestionPaiementsOpen(!isGestionPaiementsOpen)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Gestion des paiements - {section.nom}
                    {isGestionPaiementsOpen ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </CardTitle>
                </div>
                
                {/* 🆕 NAVIGATION DE PÉRIODE */}
                <div 
                  className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-lg p-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InfoBulle
                    ton="attention"
                    titre="Statistiques globales uniquement"
                    iconeClassName="text-yellow-300"
                    tailleIcone="h-5 w-5"
                  >
                    Cette navigation permet de visualiser les statistiques de paiement par mois.
                    Elle n'a <span className="font-semibold">aucun impact sur les paiements</span> que vous effectuerez.
                    Par défaut, le mois en cours est affiché.
                  </InfoBulle>

                  <button
                    onClick={goToPreviousMonth}
                    className="p-2 text-white hover:bg-white/20 rounded-md transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  <div className="flex items-center px-4 py-2 bg-white/20 rounded-md">
                    <Calendar className="h-4 w-4 mr-2 text-white" />
                    <span className="text-sm font-medium text-white capitalize whitespace-nowrap">
                      {formatDateForDisplay(selectedDate)}
                    </span>
                  </div>

                  <button
                    onClick={goToNextMonth}
                    className="p-2 text-white hover:bg-white/20 rounded-md transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  <button
                    onClick={goToCurrentMonth}
                    className="px-3 py-2 text-xs bg-white text-[#246990] rounded-md hover:bg-white/90 transition-colors font-medium"
                  >
                    Mois actuel
                  </button>
                </div>
                              </div>
            </CardHeader>

              {isGestionPaiementsOpen && (
                <CardContent className="p-6">
                  {renderClassesTable()}
                </CardContent>
              )}
            </Card>

            {/* Détail de la classe si une classe est sélectionnée */}
            {activeClass && isGestionPaiementsOpen && (
              <Card className="mt-6 border-0 shadow-sm">
                <CardHeader className="bg-[#246990] text-white rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>
                      Détail de la classe - {activeClass.nom}
                    </CardTitle>
                    <CardDescription className="text-white/80">
                      Gestion des paiements des élèves
                    </CardDescription>
                  </div>
                  
                  {/* 🆕 Sélecteur de trimestre */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-1 flex items-center gap-1">
                    {[1, 2, 3].map((trimestre) => (

                   <button
                    key={trimestre}
                    onClick={() => {
                      setSelectedTrimestreForDisplay(trimestre);
                      // 🆕 Sélectionner le premier mois du nouveau trimestre
                      const premierMois = moisScolaires.find(m => m.trimestre === trimestre);
                      if (premierMois) {
                        setSelectedMoisInTrimestre(premierMois.id);
                      }
                    }}
                    className={`px-4 py-2 rounded-md font-semibold transition-all text-sm ${
                      selectedTrimestreForDisplay === trimestre
                        ? 'bg-white text-[#246990] shadow-md'
                        : 'text-white hover:bg-white/20'
                    }`}
                  >
                    Trimestre {trimestre}
                  </button>

                    ))}
                  </div>
                </div>
              </CardHeader>

                
                <CardContent className="p-6">
                  {/* Statistiques de la classe */}


                {(() => {
                const stats = calculerStatsAvecFiltres();
                return stats && (


                <div className={`grid grid-cols-1 ${interSitesOuvert ? 'md:grid-cols-7' : 'md:grid-cols-6'} gap-4 mb-6`}>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Effectif</p>
                      <p className="text-2xl font-bold">{stats.effectif}</p>
                    </div>
                    <Users size={30} className="text-blue-500" />
                  </CardContent>
                </Card>
                
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium">Payé</p>
                      <p className="text-2xl font-bold">{stats.payes}</p>
                    </div>
                    <CheckCircle size={30} className="text-green-500" />
                  </CardContent>
                </Card>
                
                {/* 🆕 NOUVELLE CARTE : Payé au prorata */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Payé prorata</p>
                      <p className="text-2xl font-bold">{stats.payesProrata}</p>
                    </div>
                    <Calculator size={30} className="text-blue-500" />
                  </CardContent>
                </Card>
                
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-yellow-600 font-medium">Partiel</p>
                      <p className="text-2xl font-bold">{stats.partiels}</p>
                    </div>
                    <Clock size={30} className="text-yellow-500" />
                  </CardContent>
                </Card>
                
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-600 font-medium">Non payé</p>
                      <p className="text-2xl font-bold">{stats.nonPayes}</p>
                    </div>
                    <AlertCircle size={30} className="text-red-500" />
                  </CardContent>
                </Card>

                {/* « Soldé (site source) » atteste qu'un mois a ete regle a
                    l'ecole d'ou l'eleve vient. Sans inter-sites aucun transfert
                    n'est possible : ce compteur vaudrait zero a perpetuite, et
                    sa colonne resterait beante. */}
                {interSitesOuvert && (
                <Card className="bg-teal-50 border-teal-200">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-teal-600 font-medium">Soldé (site source)</p>
                      <p className="text-2xl font-bold">{stats.soldes}</p>
                    </div>
                    <CheckCircle size={30} className="text-teal-500" />
                  </CardContent>
                </Card>
                )}
                
                <Card className="bg-gray-100 border-gray-300">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-700 font-medium">Non scolarisé</p>
                      <p className="text-2xl font-bold">{stats.nonScolarises}</p>
                    </div>
                    <Users size={30} className="text-gray-500" />
                  </CardContent>
                </Card>
              </div>


                );
              })()}



                  {/* Barre de recherche et filtres */}
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex-1 min-w-[200px]">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <Input 
                          placeholder="Rechercher un élève..." 
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div className="w-[240px] relative">
                      <Calendar size={16} className="absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-500 pointer-events-none" />
                      <Select
                        value={selectedMoisInTrimestre ? String(selectedMoisInTrimestre) : undefined}
                        onValueChange={(value) => setSelectedMoisInTrimestre(parseInt(value, 10))}
                      >
                        <SelectTrigger className="h-10 rounded-md border-gray-300 bg-white pl-10 pr-3 text-sm focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20">
                          <SelectValue placeholder="Choisir un mois" />
                          <ChevronDown size={16} className="ml-2 shrink-0 text-gray-400" />
                        </SelectTrigger>
                        <SelectContent className="z-[80]">
                          {getMoisDuTrimestre().map((mois) => (
                            <SelectItem key={mois.id} value={String(mois.id)}>
                              {mois.nom}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="w-[200px] relative">
                      <Filter size={16} className="absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-500 pointer-events-none" />
                      <Select value={filterStatus || 'all'} onValueChange={setFilterStatus}>
                        <SelectTrigger className="h-10 rounded-md border-gray-300 bg-white pl-10 pr-3 text-sm focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20">
                          <SelectValue placeholder="Tous les statuts" />
                          <ChevronDown size={16} className="ml-2 shrink-0 text-gray-400" />
                        </SelectTrigger>
                        <SelectContent className="z-[80]">
                          <SelectItem value="all">Tous les statuts</SelectItem>
                          <SelectItem value="paye">Payé</SelectItem>
                          <SelectItem value="paye_prorata">Payé au prorata</SelectItem>
                          <SelectItem value="partiel">Partiel</SelectItem>
                          <SelectItem value="non_paye">Non payé</SelectItem>
                          <SelectItem value="Non scolarisé">Non scolarisé</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  
                  </div>

                  



                  
                  
                  {/* Liste des élèves */}
                  {renderElevesTable()}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          
        ))}
      </Tabs>
    )}
    
    {/* Modal de détails de paiement */}
    {/* La clé remonte le dossier quand un lien direct en ouvre un autre alors
        qu'il est déjà affiché : rien de l'élève précédent n'y reste. */}
    {isDetailsModalOpen && (
      <PaymentDetailsModal
        key={selectedEleve?.id || 'dossier'}
        isOpen={isDetailsModalOpen}
        onClose={fermerDossierFinancier}
        eleve={selectedEleve}
        anneeActive={anneeActive}
        formatMontant={formatMontant}
        onAddPayment={() => {
          fermerDossierFinancier();
          handleAddPayment(selectedEleve);
        }}
        modesPaiement={modesPaiement}
        ouvrirAuChargement={ouvertureDossier}
        onOuvertureConsommee={() => setOuvertureDossier(null)}
      />
    )}

    <PorteurNotification notification={notification} onFermer={masquer} />
    
    {/* Modal d'ajout de paiement */}
    {isAddPaymentModalOpen && (
      <AddPaymentModal 
        isOpen={isAddPaymentModalOpen} 
        onClose={() => setIsAddPaymentModalOpen(false)}
        eleve={selectedEleve}
        anneeActive={anneeActive}
        modesPaiement={modesPaiement}
        formatMontant={formatMontant}




 onSave={async (data) => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  try {
    if (activeClass) {
      console.log("🔄 Rechargement des paiements de la classe...");
      
      setIsLoadingEleves(true);
      
      // 1️⃣ Recharger les élèves avec paiements
      const response = await api.finance.getElevesClasseAvecPaiements(
        activeClass.id,
        anneeActive.id
      );
      
      if (response.success) {
        // Préparer les paiements par élève
        const paiementsParEleve = {};
        response.data.forEach(eleve => {
          paiementsParEleve[eleve.id] = eleve.paiements;
        });
        
        setTousLesPaiements(paiementsParEleve);
        
        // Préparer les élèves
        const elevesAvecInfos = response.data.map(eleve => ({
          ...eleve,
          classe_nom: activeClass.nom,
          parent_nom: eleve.parent_nom || "Non renseigné",
          parent_telephone: eleve.parent_telephone || "Non renseigné"
        }));
        
        setEleves(elevesAvecInfos);
        console.log(`✅ ${elevesAvecInfos.length} élèves rechargés après paiement`);
      }
      
      // 2️⃣ 🆕 RECHARGER AUSSI LES STATS GLOBALES
      const moisId = getMoisIdFromDate(selectedDate);
      if (moisId) {
        console.log("🔄 Rechargement des stats globales...");
        
        const statsResponse = await api.finance.getStatsPaiementsGlobales(
          anneeActive.id,
          moisId
        );
        
        if (statsResponse.success) {
          // Mettre à jour uniquement la classe active
          setClasses(prevClasses => 
            prevClasses.map(classe => {
              if (classe.id === activeClass.id) {
                const stats = statsResponse.data.find(s => s.classe_id === classe.id);
                
                if (stats) {
                  const tauxRecouvrement = stats.effectif > 0 
                    ? Math.round((stats.payes / stats.effectif) * 100) 
                    : 0;
                  
                  return {
                    ...classe,
                    effectif: stats.effectif,
                    payes: stats.payes,
                    payesProrata: stats.payes_prorata || 0,
                    soldes: stats.soldes || 0,
                    partiels: stats.partiels,
                    nonPayes: stats.non_payes,
                    nonScolarises: stats.non_scolarises,
                    tauxRecouvrement
                  };
                }
              }
              return classe;
            })
          );
          
          console.log("✅ Stats globales rechargées");
        }
      }
      
      setIsLoadingEleves(false);
    }
  } catch (error) {
    console.error("❌ Erreur lors du rafraîchissement:", error);
    setIsLoadingEleves(false);
  }
  
  console.log("=== ✅ FIN RAFRAÎCHISSEMENT ===");
}}





      />
    )}


    {/* Modal Au Prorata */}
    {showModalProrata && (
      <ModalPaiementProrata
        isOpen={showModalProrata}
        onClose={() => setShowModalProrata(false)}
        anneeActive={anneeActive}
        moisScolaires={moisScolaires}
        onSuccess={async (data) => {
          console.log('✅ Prorata enregistré:', data);
          
          // Recharger les données si une classe est active
          if (activeClass) {
            await handleClassSelect(activeClass);
          }
          
          setShowModalProrata(false);
        }}
      />
    )}
  </div>
);


};

export default GestionPaiementsEleves;
